import axios, { AxiosInstance } from 'axios';
import { logger } from '../../utils/logger';
import { tokenService, TokenService } from './auth/token.service';

class HttpClient {
  private static instance: HttpClient;
  private axiosInstance: AxiosInstance;
  private _tokenService: TokenService;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this._tokenService = tokenService;

    this.setupInterceptors();
  }

  static getInstance(): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient();
    }
    return HttpClient.instance;
  }

  private async setupInterceptors(): Promise<void> {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async config => {
        try {
          const token = await this._tokenService.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        } catch (error) {
          logger.error('Request interceptor error:', error);
          return Promise.reject(error);
        }
      },
      error => {
        logger.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          try {
            const newToken = await tokenService.refreshToken();
            if (newToken && error.config) {
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return this.axiosInstance.request(error.config);
            }
          } catch (refreshError) {
            logger.error('Token refresh failed:', refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config = {}) {
    return this.axiosInstance.get<T>(url, config);
  }

  async post<T>(url: string, data = {}, config = {}) {
    return this.axiosInstance.post<T>(url, data, config);
  }

  async put<T>(url: string, data = {}, config = {}) {
    return this.axiosInstance.put<T>(url, data, config);
  }

  async delete<T>(url: string, config = {}) {
    return this.axiosInstance.delete<T>(url, config);
  }
}

export const httpClient = HttpClient.getInstance();
